import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'common/styling/theme';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import queryString from 'query-string';
import useBackofficeBrandFilter from 'backoffice/_hooks/useBackofficeBrandFilter';
import {
  getUsersHistory,
  getTarget,
  getTotals,
  resetDahboardLoadedFlags,
  getCurrentMonthFunnels,
  getLastMonthFunnels,
  resetDashboardFirstAndSecondMonthFunnels,
} from 'common/dashboards/_redux';

import MonthPicker from 'common/components/MonthPicker';
import { BaseSelectField, PageContentPreloader } from 'common/components';

import { getDashboardAggregations } from 'common/dashboards/metrics';
import { currentDate } from 'common/dashboards/helpers';
import moment from 'moment';
import {
  FunnelChart,
  DashboardHistory,
  FulfillmentOfPlans,
  SmallAreaCharts,
  AreaChart,
  BigCharts,
  MonthColumnChart,
} from '../_components';

import { staticStyles, getDynamicStyles } from './style';

const DashboardsPage = ({
  getTotals,
  isClientInterface,
  usersHistory,
  getUsersHistory,
  target,
  targetIsLoaded,
  getTarget,
  partnerId,
  intl,
  perms,
  totals,
  history,
  location,
  match,
  viewMode,
  resetLoadedFlags,
  theme,
  sampleType,
  getCurrentMonthFunnels,
  getLastMonthFunnels,
  currentMonthFunnel,
  lastMonthFunnel,
  resetDashboardFirstAndSecondMonthFunnels,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const isBackOfficePage = match.url.indexOf('backoffice') !== -1;
  const hasBrokerAccess = perms && (perms.viewDashboards || perms.managerViewDashboard) && isBackOfficePage;
  const isBrandFilterEnabled = isBackOfficePage && match.url.indexOf('/dashboards') !== -1;
  const query = useMemo(() => queryString.parse(location.search), [location.search]);
  const brandFilter = useBackofficeBrandFilter(intl, isBrandFilterEnabled);
  const selectedBrand = isBrandFilterEnabled && query.brand ? String(query.brand) : '';
  const selectedBrandOption =
    brandFilter.options.find(option => option.value === selectedBrand) ||
    (selectedBrand ? { value: selectedBrand, label: '' } : brandFilter.options[0]);
  const dashboardBrand = selectedBrand || undefined;
  const [date, setDate] = useState(currentDate);

  const startCurrentMonth = moment(date).startOf('month').format('YYYY-MM-DD');
  const endCurrentMonth = moment(date).endOf('month').format('YYYY-MM-DD');

  const startPrevMonth = moment(date).subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
  const endPrevMonth = moment(date).subtract(1, 'month').endOf('month').format('YYYY-MM-DD');

  const tomorrow = moment(date).add(1, 'days').format('YYYY-MM-DD');

  const currentMonthName = moment(date).format('MMMM');
  const currentYear = Number(moment().format('YYYY'));
  const currentMonthNum = Number(moment().format('M'));

  const [monthValue, setMonthValue] = useState({
    year: currentYear,
    month: currentMonthNum,
  });

  const handleBrandChange = option => {
    const brand = option?.value || undefined;
    const newQuery = { ...query };

    if (brand) {
      newQuery.brand = brand;
    } else {
      delete newQuery.brand;
    }

    history.replace({
      ...location,
      search: queryString.stringify(newQuery),
    });
    resetDashboardFirstAndSecondMonthFunnels();
  };

  useEffect(() => {
    getCurrentMonthFunnels({
      startDate: startCurrentMonth,
      endDate: endCurrentMonth,
      accountableUsers: partnerId,
      sampleType,
      aggregations: getDashboardAggregations(sampleType),
      brand: dashboardBrand,
      isClientInterface,
      ...(isClientInterface && { referType: 'refer_partner' }),
    });
    getLastMonthFunnels({
      startDate: startPrevMonth,
      endDate: endPrevMonth,
      accountableUsers: partnerId,
      sampleType,
      aggregations: getDashboardAggregations(sampleType),
      brand: dashboardBrand,
      isClientInterface,
      ...(isClientInterface && { referType: 'refer_partner' }),
    });

    getUsersHistory({ hasBrokerAccess, partnerId, viewMode, brand: dashboardBrand });
    getTarget({ partnerId, sampleType, isClientInterface });
    if (!isClientInterface) {
      getTotals({ sampleType, accountableUsers: partnerId, brand: dashboardBrand, isClientInterface });
    }
  }, [
    getCurrentMonthFunnels,
    getLastMonthFunnels,
    getUsersHistory,
    getTarget,
    hasBrokerAccess,
    partnerId,
    viewMode,
    sampleType,
    date,
    dashboardBrand,
    isClientInterface,
    getTotals,
  ]);

  useEffect(() => {
    resetDashboardFirstAndSecondMonthFunnels();
  }, [dashboardBrand, partnerId, sampleType, isClientInterface, resetDashboardFirstAndSecondMonthFunnels]);

  const handleMonthChange = monthValue => {
    resetDashboardFirstAndSecondMonthFunnels();

    setMonthValue(monthValue);

    const month = monthValue.month < 10 ? `0${monthValue.month}` : monthValue.month;

    setDate(moment(`${monthValue.year}-${month}`));
  };

  useEffect(() => () => resetLoadedFlags(), [resetLoadedFlags]);

  return (
    <PageContentPreloader ready type="dashboards">
      {isBrandFilterEnabled && (
        <div className="DashboardsPage__filters">
          <BaseSelectField
            name="brand"
            textId="justBrand"
            value={selectedBrandOption}
            onChange={handleBrandChange}
            options={brandFilter.options}
            searchable
          />
        </div>
      )}
      {!isClientInterface && (
        <div className="DashboardsPage__totals">
          {totals?.totalBalance && (
            <div className="DashboardsPage__total-card">
              <FormattedMessage id="justTotalCrmBalance">
                {txt => <div className="DashboardsPage__total-title">{txt}</div>}
              </FormattedMessage>
              <div className="DashboardsPage__total-number">&#36; {`${totals.totalBalance}`}</div>
            </div>
          )}

          {typeof totals?.totalClients === 'number' && (
            <div className="DashboardsPage__total-card">
              <FormattedMessage id="justTotalClients">
                {txt => <div className="DashboardsPage__total-title">{txt}</div>}
              </FormattedMessage>
              <div className="DashboardsPage__total-number">{`${totals?.totalClients}`}</div>
            </div>
          )}
        </div>
      )}

      <div className="TopMonthPicker">
        <MonthPicker
          monthValue={monthValue}
          years={{ min: { year: 2010, month: 1 }, max: { year: currentYear, month: currentMonthNum } }}
          handleChange={handleMonthChange}
        />
      </div>
      <div className="DashboardsPage__funnel">
        <div className="DashboardsPage__funnel-top">
          <FormattedMessage id="boFunnelFrom" values={{ month: currentMonthName }}>
            {txt => <h3>{txt}</h3>}
          </FormattedMessage>
        </div>
        <div className="DashboardsPage__funnel-top-wrapper">
          <FunnelChart currentMonthFunnel={currentMonthFunnel?.aggregated_data} />
          {Object.keys(usersHistory).length > 0 && (
            <DashboardHistory usersHistory={usersHistory} isClientInterface={isClientInterface} />
          )}
        </div>
      </div>
      <FulfillmentOfPlans
        currentMonthFunnel={currentMonthFunnel?.aggregated_data}
        lastMonthFunnel={lastMonthFunnel?.aggregated_data}
        target={target}
        targetIsLoaded={targetIsLoaded}
        partnerId={partnerId}
        viewMode={viewMode}
        currentMonthName={currentMonthName}
        sampleType={sampleType}
        isClientInterface={isClientInterface}
      />
      <div className="DashboardsPage__month">
        <FormattedMessage id="boDashboardThisMonth">
          {txt => <h3 className="DashboardsPage__month-title">{txt}</h3>}
        </FormattedMessage>
        <div className="DashboardsPage__month-topCharts">
          <MonthColumnChart
            key={`${startCurrentMonth}-${dashboardBrand}-${sampleType}-${partnerId}-${isClientInterface}`}
            partnerId={partnerId}
            sampleType={sampleType}
            date={date}
            brand={dashboardBrand}
            isClientInterface={isClientInterface}
          />
          <AreaChart
            currentMonthFunnel={currentMonthFunnel?.aggregated_data}
            lastMonthFunnel={lastMonthFunnel?.aggregated_data}
            dailyFunnels={currentMonthFunnel?.aggregated_data_by_day}
            tomorrow={tomorrow}
          />
        </div>
        <SmallAreaCharts
          currentMonthFunnel={currentMonthFunnel?.aggregated_data}
          lastMonthFunnel={lastMonthFunnel?.aggregated_data}
          dailyFunnels={currentMonthFunnel?.aggregated_data_by_day}
          tomorrow={tomorrow}
        />
      </div>
      <BigCharts
        currentMonthFunnel={currentMonthFunnel?.aggregated_data}
        dailyFunnels={currentMonthFunnel?.aggregated_data_by_day}
        displayToday={moment(date).format('DD.MM.YYYY')}
        startOfWeek={moment(date).startOf('week').format('YYYY-MM-DD')}
        endOfWeek={moment(date).endOf('week').format('YYYY-MM-DD')}
        sampleType={sampleType}
      />
      <style jsx>{staticStyles}</style>
      <style jsx>{dynamicStyles}</style>
    </PageContentPreloader>
  );
};

DashboardsPage.propTypes = {
  getCurrentMonthFunnels: PropTypes.func.isRequired,
  getLastMonthFunnels: PropTypes.func.isRequired,
  currentMonthFunnel: PropTypes.object,
  lastMonthFunnel: PropTypes.object,
  isClientInterface: PropTypes.bool,
  usersHistory: PropTypes.object,
  getUsersHistory: PropTypes.func.isRequired,
  target: PropTypes.object,
  totals: PropTypes.object,
  targetIsLoaded: PropTypes.bool.isRequired,
  getTarget: PropTypes.func.isRequired,
  getTotals: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  partnerId: PropTypes.number,
  perms: PropTypes.object.isRequired,
  resetLoadedFlags: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  viewMode: PropTypes.string,
  theme: PropTypes.object,
  sampleType: PropTypes.string.isRequired,
  resetDashboardFirstAndSecondMonthFunnels: PropTypes.func.isRequired,
};

DashboardsPage.defaultProps = {
  currentMonthFunnel: {},
  lastMonthFunnel: {},
  isClientInterface: false,
  target: {},
  totals: {},
  partnerId: 0,
  usersHistory: {},
  viewMode: null,
  theme: {},
};

export default compose(
  withRouter,
  injectIntl,
  withTheme(),
  connect(
    state => ({
      currentMonthFunnel: state.dashboards.currentMonthFunnel,
      lastMonthFunnel: state.dashboards.lastMonthFunnel,
      usersHistory: state.dashboards.usersHistory,
      usersHistoryIsLoaded: state.dashboards.usersHistoryIsLoaded,
      totals: state.dashboards.totals,
      totalsIsLoaded: state.dashboards.totalsIsLoaded,
      target: state.dashboards.target,
      targetIsLoaded: state.dashboards.targetIsLoaded,
      perms: state.user.profile.perms,
    }),
    {
      getCurrentMonthFunnels: data => getCurrentMonthFunnels.request(data),
      getLastMonthFunnels: data => getLastMonthFunnels.request(data),
      getUsersHistory: data => getUsersHistory.request(data),
      getTarget: data => getTarget.request(data),
      getTotals: data => getTotals.request(data),
      resetLoadedFlags: () => resetDahboardLoadedFlags(),
      resetDashboardFirstAndSecondMonthFunnels: () => resetDashboardFirstAndSecondMonthFunnels(),
    }
  )
)(DashboardsPage);
export { DashboardsPage };
